import moment from "moment";
import Swal from "sweetalert2";
import DOMPurify from "dompurify";
import parse from "html-react-parser";

import Strings from "../constants/strings";
import { Language } from "../constants/enums";
import { ASSET_API_URL, Constants } from "../constants/constants";
import { ICodename, IMultiLanguageContent, IResourcePermission, IUser } from "./interfaces";
import Resources from "../constants/resources";

const Helpers = {
    /**
     * Check Object is null or String null or empty.
     *
     * @param {object | string} value Object or String
     * @returns {boolean} if null or empty return true, otherwise return false.
     */
    isNullOrEmpty: (value: any): value is undefined | boolean => {
        if (Array.isArray(value) && value.length === 0) {
            return true;
        } else return value === undefined || value === null || value === "";
    },

    /**
     * Check value is array or non.
     *
     * @param {any} value: The value to be tested.
     * @returns {boolean} If data type is array true. Otherwise it returns false.
     */
    isArray: (value: any): value is (object | string | number | boolean)[] => {
        return Array.isArray(value);
    },

    /**
     * Check a value is number or non, if number then return true, otherwise return false.
     *
     * @param {string} value: Value can check number
     * @returns {boolean} if number then return true, otherwise return false.
     */
    isNumber: (value: any): value is number => {
        return typeof value === "number";
    },

    /**
     * Check a value is NaN or not
     *
     * @param {any} value: Value can check number
     * @returns {boolean} if NaN then return true, otherwise return false.
     */
    isNaN: (value: any): value is number => {
        return isNaN(value);
    },

    isFunction: (obj: any): obj is Function => {
        return typeof obj === "function";
    },

    isValidPhoneNumber: (phoneNumber: string) => {
        // return Constants.RegExp.PHONE_NUMBER.test(phoneNumber);
        // let array = phoneNumber?.split("-") || [];
        // let index = phoneCode.findIndex((item) => item.code === array?.[0]);
        // return array.length === 2 && index !== -1 && array?.[1]?.length >= 8;

        return phoneNumber.length >= 8;
    },

    isValidEmail: (email: string) => {
        return Constants.RegExp.NEW_EMAIL_ADDRESS.test(email);
    },

    /**
     * Convert size in bytes to KB, MB, GB or TB
     *
     * @param {number} bytes: Size convert
     * @returns {string} Value formatted include unit.
     */
    bytesToSize: (bytes: number): string => {
        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
        if (Helpers.isNullOrEmpty(bytes) || bytes === 0) {
            return "0 Byte";
        }
        const i = Math.floor(Math.floor(Math.log(bytes) / Math.log(1024)));
        return `${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]}`;
    },

    stringToSizeByte: (text: string) => {
        return new TextEncoder().encode(text).length;
    },

    clsxObject: (obj: Record<string, string | undefined | boolean>) => {
        const outClassNameArray: string[] = [];
        Object.keys(obj).forEach((key) => {
            if (!!obj[key]) {
                outClassNameArray.push(key);
            }
        });

        return outClassNameArray.join(" ").trim().replace(/\s+/, " ");
    },

    formatCurrency: (number: number | string = 0, decimals: number = 0) => {
        if (typeof number == "number" || !isNaN(parseInt(number))) {
            let a = Number(number).toFixed(decimals).split(".");
            a[0] = a[0].replace(/\d(?=(\d{3})+$)/g, "$&,");
            if (Number(number) < 0) {
                return `${a.join(".")}`;
            } else {
                return a.join(".");
            }
        }

        return undefined;
    },

    getMultiContentLanguage: function (multiLanguageContent: any, lang?: string, fallbackLang?: "vi" | "en" | "any") {
        const multiLanguageContentValue = { ...multiLanguageContent?.value };
        const defaultLanguage = Strings.getLanguage();

        if (typeof multiLanguageContent === "string") return multiLanguageContent;

        let outText: string = multiLanguageContent;
        if (typeof multiLanguageContent === "object" && multiLanguageContent?.value !== undefined) {
            if (lang) {
                outText = multiLanguageContentValue[lang];
            } else {
                outText = multiLanguageContentValue[defaultLanguage] || multiLanguageContentValue["vi"];
            }

            if (fallbackLang && !outText) {
                if (fallbackLang === "any") {
                    // Prioritize for Vietnamese content than other languages
                    // Just able when lang is not Vietnamese
                    if (lang !== Language.Vietnamese && multiLanguageContentValue[Language.Vietnamese]) {
                        outText = multiLanguageContentValue[Language.Vietnamese];
                    } else {
                        delete multiLanguageContentValue[Language.Vietnamese];
                        const multiLangs = Object.entries<string>(multiLanguageContentValue);
                        if (multiLangs.length > 0) {
                            outText = multiLangs[0][1];
                        }
                    }
                } else {
                    outText = multiLanguageContent.value[fallbackLang];
                }
            }
        }
        return outText || "";
    },

    isDev: (): boolean => {
        return process.env.NODE_ENV !== "production";
    },

    handleFormatParams(data: any) {
        const params = new URLSearchParams();
        Object.entries(data).forEach(([key, values]) => {
            if (!Helpers.isNullOrEmpty(values)) {
                if (Array.isArray(values)) {
                    if (values.length > 0) {
                        values.forEach((value) => {
                            if (!Helpers.isNullOrEmpty(value)) {
                                params.append(key, value.toString());
                            }
                        });
                    }
                } else {
                    params.append(key, `${values}`);
                }
            }
        });
        return params.toString();
    },

    formatDate: (value?: string | Date | number, format?: string): string => {
        const result = value
            ? moment(value)
                .local()
                .format(format || "DD/MM/YYYY")
            : "";
        return result;
    },

    showAlert: async (message: string, type?: "warning" | "success" | "error" | "info" | "question", okCallback?: any) => {
        const msg = message;
        const okPress = await Swal.fire({
            customClass: {
                container: "custom-sweetalert2",
                confirmButton: "custom-sweetalert2-btn-ok",
            },
            text: msg,
            icon: type,
            allowOutsideClick: false,
            confirmButtonText: Strings.Common.ACCEPT,
        });
        if (okPress && okPress.isConfirmed && okCallback && Helpers.isFunction(okCallback)) {
            okCallback();
        }
    },

    showConfirmAlert: async (message: string, okCallback: any, cancelCallback?: any, okButtonMessage?: string, cancelButtonMessage?: string) => {
        const msg = message;
        const okPress = await Swal.fire({
            customClass: {
                container: "custom-sweetalert2",
                confirmButton: "custom-sweetalert2-btn-ok",
                cancelButton: "custom-sweetalert2-btn-cancel",
            },
            text: msg,
            icon: "warning",
            reverseButtons: true,
            showCancelButton: true,
            confirmButtonText: okButtonMessage || "Ok",
            cancelButtonText: cancelButtonMessage || Strings.Common.CANCEL,
        });
        if (okPress && okPress.isConfirmed && okCallback && Helpers.isFunction(okCallback)) {
            okCallback();
        } else {
            if (cancelCallback && Helpers.isFunction(cancelCallback)) {
                cancelCallback();
            }
        }
    },

    getPageNumber: (pageNumber: number, pageSize: number, totalCount: number) => {
        const mathCeil = Math.ceil((totalCount || 0) / pageSize);
        const valTemp = mathCeil > 0 ? mathCeil : 1;
        return pageNumber > valTemp ? valTemp : pageNumber;
    },

    renderExceptionError: (error?: any) => {
        let mesErr = undefined;
        let validationErr = undefined;

        if (error?.message?.responseException?.validationErrors) {
            validationErr = (error?.message?.responseException?.validationErrors || [])[0].message;
        }

        if (error?.message?.responseException?.exceptionMessage) {
            mesErr = error?.message?.responseException?.exceptionMessage || error?.responseException?.exceptionMessage;
        }

        const message: string = mesErr || validationErr || error?.message?.message || error?.message?.Message || Strings.Common.ERROR;

        return message === "Server Error" ? Strings.Common.ERROR : message;
    },

    handleValidationError: (error: any) => {
        let newErrors: { [key: string]: string } = {};

        if (error.name === "ValidationError") {
            for (const e of error.inner) {
                const path = `${e.path}`.split(".");
                newErrors[path[0]] = e.errors[0];
            }
        }
        return newErrors;
    },

    checkMaxLengthTypeNumber: (value: any, maxLength: number) => {
        let result = value.toString().slice(0, maxLength);
        return result;
    },

    renderValueByLanguage: (props: { data?: IMultiLanguageContent; currentLanguage: string }) => {
        if (Helpers.isNullOrEmpty(props.data?.value) || Object.keys(props.data?.value).length === 0) {
            return "";
        } else {
            return Helpers.isNullOrEmpty(props.data?.value?.[props.currentLanguage])
                ? props.data?.value?.[Constants.DefaultLanguage]
                : props.data?.value?.[props.currentLanguage];
        }
    },

    getDataMultiLanguage: (props: { data: IMultiLanguageContent | undefined; currentLanguage: string; listLanguage: ICodename[] }) => {
        if (Helpers.isNullOrEmpty(props.data)) {
            return undefined;
        } else {
            let newData: any = { ...props.data };
            props.listLanguage.forEach((e) => {
                if (Helpers.isNullOrEmpty(newData.value?.[`${e.code}`])) {
                    const newValue = {
                        ...newData.value,
                        [`${e.code}`]: newData.value?.[props.currentLanguage],
                    };
                    newData = { ...newData, value: newValue };
                }
            });
            return newData as IMultiLanguageContent;
        }
    },

    handleTrimSpaceForwardSlash: (inputString: string): string => {
        if (inputString != null && inputString.length > 0) {
            let modifiedString = inputString;
            while (
                !Helpers.isNullOrEmpty(modifiedString) &&
                (modifiedString[0] === " " ||
                    modifiedString[0] === "/" ||
                    modifiedString[modifiedString.length - 1] === " " ||
                    modifiedString[modifiedString.length - 1] === "/")
            ) {
                modifiedString = modifiedString.trim();
                modifiedString = modifiedString.replace(/^\/+/, "").replace(/\/+$/, "");
            }
            return modifiedString;
        } else {
            return "";
        }
    },

    handleUrlJoin: (...parts: (string | undefined | null)[]): string => {
        const urlParts: (string | undefined | null)[] = parts
            .filter((part) => !Helpers.isNullOrEmpty(part) && part && part.length > 0)
            .map((part) => part && Helpers.handleTrimSpaceForwardSlash(part))
            .filter((part) => !Helpers.isNullOrEmpty(part) && part && part.length > 0);

        return urlParts.join("/");
    },

    getFileExtesion: (fileUrl?: string) => {
        if (!Helpers.isNullOrEmpty(fileUrl)) {
            let extension = fileUrl.slice(fileUrl.lastIndexOf("."), fileUrl.length);
            if (extension.includes("?")) {
                extension = extension.split("?")[0];
            }
            return extension.toLowerCase();
        } else {
            return "";
        }
    },

    isYoutubeUrl: (url?: string): boolean => {
        if (Helpers.isNullOrEmpty(url)) {
            return false;
        }
        if (url.includes("maysoft")) {
            return false;
        }
        return url.trim().toLowerCase().replace(".", "").includes("youtube");
    },

    getImageUrlByFileId: (fileId: string) => {
        if (Helpers.isNullOrEmpty(fileId) || fileId === "0") {
            return Resources.IMAGES.THUMBNAIL;
        } else {
            return Helpers.getFileAccessUrl(fileId);
        }
    },

    getFileAccessUrl: (fileId?: string) => {
        if (Helpers.isNullOrEmpty(fileId) || fileId === "0") {
            return "";
        }
        return ASSET_API_URL + Constants.ApiPath.ASSET.GET_ASSET_ACCESS_URL + `/${fileId}`;
    },

    // Xóa dấu trong chuỗi
    removeAccentsFromStrings: (str: string) => {
        // Gộp nhiều dấu space thành 1 space
        str = str.replace(/\s+/g, " ");
        // loại bỏ toàn bộ dấu space (nếu có) ở 2 đầu của chuỗi
        str = str.trim();

        // bắt đầu xóa dấu tiếng việt trong chuỗi
        str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, "a");
        str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, "e");
        str = str.replace(/ì|í|ị|ỉ|ĩ/g, "i");
        str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, "o");
        str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, "u");
        str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, "y");
        str = str.replace(/đ/g, "d");
        str = str.replace(/À|Á|Ạ|Ả|Ã|Â|Ầ|Ấ|Ậ|Ẩ|Ẫ|Ă|Ằ|Ắ|Ặ|Ẳ|Ẵ/g, "A");
        str = str.replace(/È|É|Ẹ|Ẻ|Ẽ|Ê|Ề|Ế|Ệ|Ể|Ễ/g, "E");
        str = str.replace(/Ì|Í|Ị|Ỉ|Ĩ/g, "I");
        str = str.replace(/Ò|Ó|Ọ|Ỏ|Õ|Ô|Ồ|Ố|Ộ|Ổ|Ỗ|Ơ|Ờ|Ớ|Ợ|Ở|Ỡ/g, "O");
        str = str.replace(/Ù|Ú|Ụ|Ủ|Ũ|Ư|Ừ|Ứ|Ự|Ử|Ữ/g, "U");
        str = str.replace(/Ỳ|Ý|Ỵ|Ỷ|Ỹ/g, "Y");
        str = str.replace(/Đ/g, "D");

        // Some system encode vietnamese combining accent as individual utf-8 characters
        // Một vài bộ encode coi các dấu mũ, dấu chữ như một kí tự riêng biệt nên thêm hai dòng này
        str = str.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, ""); // ̀ ́ ̃ ̉ ̣  huyền, sắc, ngã, hỏi, nặng
        str = str.replace(/\u02C6|\u0306|\u031B/g, ""); // ˆ ̆ ̛  Â, Ê, Ă, Ơ, Ư
        // Remove extra spaces
        // Bỏ các khoảng trắng liền nhau
        str = str.replace(/ + /g, " ");
        str = str.trim();
        // Remove punctuations
        // Bỏ dấu câu, kí tự đặc biệt
        str = str.replace(/!|@|%|\^|\*|\(|\)|\+|\=|\<|\>|\?|\/|,|\.|\:|\;|\'|\"|\&|\#|\[|\]|~|\$|_|`|-|{|}|\||\\/g, " ");

        return str;
    },

    stringToHTML: (text: string) => {
        return parse(DOMPurify.sanitize(text));
    },

    capitalizeFirstLetter(str: string) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    },

    getCharacterAvatar: (fullName?: string): string => {
        if (!fullName) {
            return "";
        }

        const itemNames = fullName.split(" ").filter((value: string) => {
            return value.trim().length > 0;
        });

        let fName = "";

        if (itemNames.length >= 2) {
            for (let i = itemNames.length - 2; i < itemNames.length; i++) {
                if (itemNames[i].length > 0) {
                    fName = fName + itemNames[i].substring(0, 1).toUpperCase();
                }
            }
        } else {
            fName = itemNames[0].substring(0, 2).toUpperCase();
        }

        return fName;
    },

    setLocalStorage: (storageKey: string, value: any) => {
        localStorage.setItem(storageKey, JSON.stringify(value));
    },

    getLocalStorage: (storageKey: string, defaultValue?: any): any => {
        const value = localStorage.getItem(storageKey);
        if (!Helpers.isNullOrEmpty(value) && value !== "undefined" && value !== "null") {
            return Helpers.converStringToJson(value);
        } else {
            return defaultValue;
        }
    },

    converStringToJson: (value?: any): any => {
        if (Helpers.isNullOrEmpty(value) || value === "undefined" || value === "null") {
            return undefined;
        } else {
            try {
                const newValue = JSON.parse(value);
                return newValue;
            } catch (error) {
                return undefined;
            }
        }
    },

    getDefaultValueMultiLanguage: (data: IMultiLanguageContent | undefined, currentLanguage: string) => {
        if (Helpers.isNullOrEmpty(data)) return "";
        if (Helpers.isNullOrEmpty(data.value?.[currentLanguage]) && !Helpers.isNullOrEmpty(data?.value?.[Constants.DefaultLanguage]))
            return data?.value?.[Constants.DefaultLanguage];
        if (Helpers.isNullOrEmpty(data.value?.[currentLanguage])) return "";
        return data.value?.[currentLanguage];
    },

    getFullName: (user: IUser) => {
        if (!Helpers.isNullOrEmpty(user.organizationUserProfile?.firstName) && !Helpers.isNullOrEmpty(user.organizationUserProfile?.lastName)) {
            const { firstName, lastName } = user.organizationUserProfile;
            return [lastName, firstName].join(" ");
        }
        return user?.fullName || user?.userName;
    },
};

export default Helpers;
